import type { TraxTile } from '../games/trax';
import './TraxPlaySheet.css';

// Winning shapes checked against real engine fixtures. These are pictures,
// never a second board, legal-move calculator, or interactive match control.
const TILE_PATHS: Record<TraxTile, [string, string]> = {
  WhiteNS: ['M50 0V100', 'M0 50H100'],
  WhiteEW: ['M0 50H100', 'M50 0V100'],
  WhiteNE: ['M50 0A50 50 0 0 0 100 50', 'M50 100A50 50 0 0 0 0 50'],
  WhiteES: ['M100 50A50 50 0 0 0 50 100', 'M0 50A50 50 0 0 0 50 0'],
  WhiteSW: ['M50 100A50 50 0 0 0 0 50', 'M50 0A50 50 0 0 0 100 50'],
  WhiteWN: ['M0 50A50 50 0 0 0 50 0', 'M100 50A50 50 0 0 0 50 100'],
};

function Tile({ x, y, tile, size = 46, muted = false, winningColor }: {
  x: number; y: number; tile: TraxTile; size?: number; muted?: boolean; winningColor?: 'white' | 'red';
}) {
  const [white, red] = TILE_PATHS[tile];
  const base = '#252725';
  return <g transform={`translate(${x} ${y}) scale(${size / 100})`} data-tile={tile}>
    <rect width="100" height="100" fill={base} />
    <path d={red} stroke={muted || winningColor === 'white' ? '#95766f' : '#d94335'} strokeWidth="17" fill="none" />
    <path d={white} stroke={base} strokeWidth="23" fill="none" />
    <path d={white} stroke={muted || winningColor === 'red' ? '#b8b5ad' : '#fffdf8'} strokeWidth="17" fill="none" />
  </g>;
}

function Loop() {
  return <svg className="trax-paper-diagram" viewBox="24 0 296 94" role="img"
    aria-label="4枚のタイルを通る白い線が閉じ、輪になっています。白の勝ちです。" data-diagram-phase="loop">
    <Tile x={72} y={0} tile="WhiteES" /><Tile x={119} y={0} tile="WhiteSW" />
    <Tile x={72} y={47} tile="WhiteNE" /><Tile x={119} y={47} tile="WhiteWN" />
    <path d="M143 22H180" stroke="#73766e" fill="none" />
    <text x="193" y="20">白の勝ちです。</text>
  </svg>;
}

const HORIZONTAL: (TraxTile | null)[] = ["WhiteES", "WhiteWN", "WhiteNS", "WhiteNS", "WhiteNE", "WhiteSW", "WhiteNE", null, "WhiteNS", "WhiteES", "WhiteWN", "WhiteNE", "WhiteSW", "WhiteNE", "WhiteSW", null, null, null, null, "WhiteSW", "WhiteNE", "WhiteSW", "WhiteNS", "WhiteNS"];
const VERTICAL: (TraxTile | null)[] = [null, "WhiteNS", "WhiteNE", null, "WhiteNE", "WhiteSW", null, "WhiteSW", "WhiteNS", "WhiteES", "WhiteWN", "WhiteNS", "WhiteWN", "WhiteES", "WhiteWN", "WhiteES", "WhiteWN", "WhiteES", "WhiteNS", "WhiteES", "WhiteWN", "WhiteNS", null, null];
const HORIZONTAL_PATH = new Set([1, 9, 8, 2, 3, 4, 12, 13, 21, 22, 23]);
const VERTICAL_PATH = new Set([1, 4, 5, 8, 11, 14, 13, 16, 15, 18, 21]);

function AcrossEdges() {
  return <svg className="trax-paper-diagram" viewBox="24 0 296 198" role="img"
    aria-label="幅8マスで曲がりくねった赤の線が左右の端をつなぐ形と、高さ8マスで曲がりくねった白の線が上下の端をつなぐ形は勝ちです。線は11枚を通りますが、測るのはタイル全体の幅と高さです。" data-diagram-phase="across">
    <text x="80" y="14" className="trax-paper-dimension">幅8マス</text>
    <path d="M32 28V24H183V28" stroke="#73766e" fill="none" />
    <g data-example="horizontal">
      {HORIZONTAL.map((tile, i) => tile && <g key={i} data-diagram-index={i}>
        <Tile x={32 + 19 * (i % 8)} y={34 + 19 * Math.floor(i / 8)} size={18} tile={tile}
          muted={!HORIZONTAL_PATH.has(i)} winningColor="red" />
      </g>)}
    </g>
    <path d="M24 62H32M183 81H191" stroke="#252725" strokeWidth="5.4" />
    <path d="M24 62H32M183 81H191" stroke="#d94335" strokeWidth="3.06" />
    <text x="32" y="113">赤の勝ちです。</text>
    <text x="255" y="14" className="trax-paper-dimension">高さ8マス</text>
    <g data-example="vertical">
      {VERTICAL.map((tile, i) => tile && <g key={i} data-diagram-index={i}>
        <Tile x={258 + 19 * (i % 3)} y={25 + 19 * Math.floor(i / 3)} size={18} tile={tile}
          muted={!VERTICAL_PATH.has(i)} winningColor="white" />
      </g>)}
    </g>
    <path d="M286 19V25M267 176V182" stroke="#252725" strokeWidth="5.4" />
    <path d="M286 19V25M267 176V182" stroke="#fffdf8" strokeWidth="3.06" />
    <path d="M315 25H318V176H296" stroke="#73766e" fill="none" />
    <text x="229" y="194" className="trax-paper-dimension">白の勝ちです。</text>
  </svg>;
}

function AutomaticPlacement() {
  return <svg className="trax-paper-diagram" viewBox="24 -2 296 74" role="img"
    aria-label="置く前は下に2枚のタイルがあります。左上に1枚置くと、右上の空きマスに白い線が左と下の2方向から入り、右上にもタイルが自動で置かれます。左上の実線の枠が自分で置いた1枚、右上の点線の枠が自動の1枚です。" data-diagram-phase="automatic">
    <g data-example="automatic-before">
      <rect x="48" y="0.5" width="24" height="23.5" fill="none" stroke="#92938e" strokeDasharray="3 3" />
      <rect x="77" y="0.5" width="24" height="23.5" fill="none" stroke="#92938e" strokeDasharray="3 3" />
      <Tile x={48} y={29} size={24} tile="WhiteWN" /><Tile x={77} y={29} size={24} tile="WhiteNS" />
    </g>
    <g data-example="automatic-after">
      <Tile x={198} y={0} size={24} tile="WhiteES" /><Tile x={227} y={0} size={24} tile="WhiteSW" />
      <Tile x={198} y={29} size={24} tile="WhiteWN" /><Tile x={227} y={29} size={24} tile="WhiteNS" />
      <rect x="196.5" y="-1.5" width="27" height="27" fill="none" stroke="#343a32" strokeWidth="1.5" />
      <rect x="225.5" y="-1.5" width="27" height="27" fill="none" stroke="#343a32" strokeWidth="1.5" strokeDasharray="2 2" />
    </g>
    <path d="M72 41H77M222 41H227M222 12H227M210 24V29M239 24V29" stroke="#252725" strokeWidth="7" />
    <path d="M72 41H77M222 41H227" stroke="#d94335" strokeWidth="4.08" />
    <path d="M222 12H227M210 24V29M239 24V29" stroke="#fffdf8" strokeWidth="4.08" />
    <path d="M196.5 12H190V55M252.5 12H275V55" stroke="#73766e" fill="none" />
    <text x="49" y="68" className="trax-paper-dimension">置く前</text>
    <text x="151" y="68" className="trax-paper-dimension">置いた1枚</text>
    <text x="242" y="68" className="trax-paper-dimension">自動の1枚</text>
  </svg>;
}

export function TraxPlaySheet() {
  return <article className="trax-paper">
    <p className="trax-paper-intro">先手は白、後手は赤です。<br />二人とも同じタイルを使います。</p>
    <div className="trax-paper-sequence">
      <section>
        <h2>自分の線で輪を作ると勝ちです。</h2>
        <figure><Loop /></figure>
      </section>
      <section>
        <h2>タイル全体の左右か上下の端を<br />自分の線でつなぐと勝ちです。</h2>
        <figure><AcrossEdges />
          <figcaption>左右なら幅8マス以上、上下なら高さ8マス以上必要です。</figcaption>
        </figure>
      </section>
    </div>
    <div className="trax-paper-rules">
      <p>交互に1枚ずつ置きます。2枚目からは辺どうしをつけて、接する線の色を合わせます。</p>
      <figure className="trax-paper-automatic"><p>同じ色の線が2方向から入る空きマスには<br />自動でタイルが置かれます。</p><AutomaticPlacement /></figure>
    </div>
  </article>;
}
